import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { createMutationErrorNet } from "@/lib/mutationErrorNet";
import { shouldRetryQuery } from "@/lib/queryRetry";
import { useAuth } from "./hooks/useAuth";
import { useGoogleAccountGuard } from "./hooks/useGoogleAccountGuard";
import { OrbState } from "@/components/ui/orb-state";
import { PageLoader } from "./components/ui/page-loader";
import { LazyLoadErrorBoundary } from "./components/error-boundaries/LazyLoadErrorBoundary";
import { routeRegistry } from "./routes/registry";
import { bootsIntoAppShell, findBootRoute, preloadRouteChunk } from "./routes/preload";
import { RouteGateGuard } from "./routes/RouteGateGuard";
import type { PageRouteDef, RedirectRouteDef, RouteDef } from "./routes/types";
import { LocationSessionProvider } from "./modules/location-session/LocationSessionContext";
import { useLocationPushRebind } from "./modules/location-session/hooks/useLocationPushRebind";
import { LocationGate } from "./modules/location-session/components/LocationGate";
import { LocationRouteEnforcer } from "./modules/location-session/components/LocationRouteEnforcer";
import { RestrictedRouteEnforcer } from "./routes/RestrictedRouteEnforcer";
import { AccountantRouteEnforcer } from "./routes/AccountantRouteEnforcer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { bindTrailToUser } from "@/lib/actionTrail";
import { onAccountChange } from "@/lib/accountChange";
import { resetVisibleChildIdUniverse } from "@/services/shared/tisaExclusion";
import { useAnalytics } from "./hooks/useAnalytics";

// Boot preload (docs/plans/2026-10-15-lms-load-speed.md §4 Phase 1): start the
// shell and the boot route's page chunk downloading now, so they overlap the
// gate chain (identity → roles → location, ~1s) instead of starting after it.
// This only warms the module cache — LocationGate, the route enforcers and
// RouteGateGuard still decide what mounts and when. Public, kiosk and widget
// paths never mount Layout, so they never download it.
const bootRoute = findBootRoute(routeRegistry, window.location.pathname);
const layoutModule = bootsIntoAppShell(bootRoute) ? import("./components/Layout") : null;
// Mark the warm-up handled: a failed chunk surfaces when Layout actually
// renders (the lazy below awaits this same promise → LazyLoadErrorBoundary).
layoutModule?.catch(() => undefined);
preloadRouteChunk(bootRoute);

// The Layout shell isn't a routed page — it isn't in the registry, which
// only models GuardedLayout's children — so it keeps its own lazy
// declaration here, same as before the registry existed.
const Layout = lazy(() =>
  (layoutModule ?? import("./components/Layout")).then(m => ({ default: m.Layout })),
);
const MockRouteLayout = __MOCK_ROUTES_ENABLED__
  ? lazy(() => import("./components/mock/MockLayout").then((m) => ({ default: m.MockRouteLayout })))
  : null;

// =====================================================
// ROUTE TREE — derived from src/routes/registry.tsx
// Every page/redirect element below comes from the registry; only the
// auth-shell branching (the isAuthenticated ternary + the unauthenticated
// fallback redirects) is hand-written here — that branching is App.tsx's
// own concern, not a routed surface the registry models.
// =====================================================

function findPage(path: string): PageRouteDef {
  const route = routeRegistry.find((r) => r.path === path);
  if (!route || route.kind !== "page") {
    throw new Error(`routes/registry: expected a page route at "${path}"`);
  }
  return route;
}

function findRedirect(path: string): RedirectRouteDef {
  const route = routeRegistry.find((r) => r.path === path);
  if (!route || route.kind !== "redirect") {
    throw new Error(`routes/registry: expected a redirect route at "${path}"`);
  }
  return route;
}

/**
 * Renders one registry entry as a <Route>. `nested` strips the leading "/"
 * so the path is valid as a child of the "/" GuardedLayout route — React
 * Router v6 nested route paths are relative, and every authenticated route
 * in the registry is exactly one level deep, so a single slice suffices.
 */
function routeFor(route: RouteDef, { nested = false }: { nested?: boolean } = {}): JSX.Element {
  const path = nested ? route.path.slice(1) : route.path;
  if (route.kind === "redirect") {
    return <Route key={route.path} path={path} element={<Navigate to={route.redirectTo} replace />} />;
  }
  const Element = route.element;
  return (
    <Route
      key={route.path}
      path={path}
      element={
        <RouteGateGuard gate={route.gate} routePath={route.path}>
          <Element />
        </RouteGateGuard>
      }
    />
  );
}

const PUBLIC_ROUTES = routeRegistry.filter(
  (r): r is PageRouteDef => r.kind === "page" && r.gate === "public",
);

const AUTHENTICATED_ROUTES = routeRegistry.filter(
  (r) =>
    r.path !== "/" &&
    r.path !== "*" &&
    !(r.kind === "page" && r.gate === "public"),
);

const { element: RootRoute } = findPage("/");
const { element: NotFound } = findPage("*");

const queryClient = new QueryClient({
  mutationCache: createMutationErrorNet(),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: shouldRetryQuery,
    },
  },
});

// Shared devices (kiosk iPads) must never flash a prior account's cached
// data after sign-out → different sign-in. The whole query cache is cleared
// on sign-out AND whenever the signed-in user id changes without one (a
// second account signing in through /auth); token refreshes clear nothing.
// The bug-report action trail is bound to the signed-in user on every event,
// including the first of a page load, so a trail persisted by another
// account (an OAuth redirect into a different account) is dropped. Registered
// once at module scope (not inside useAuth's per-mount effect) so it fires
// exactly once per page load regardless of how many components mount the
// auth hook. The TISA-exclusion visible-id read is shared across concurrent
// callers outside the query cache, so it is detached here too.
const clearCacheOnAccountChange = onAccountChange(() => {
  queryClient.clear();
  resetVisibleChildIdUniverse();
});
supabase.auth.onAuthStateChange((event, session) => {
  bindTrailToUser(session?.user?.id ?? null);
  clearCacheOnAccountChange(event, session);
});

/**
 * Wraps the authenticated Layout with the unrecognized-Google-account guard.
 * Rendered under <QueryClientProvider> (the guard uses react-query), which is
 * why the guard cannot live in useAuth / App's top-level call site — those sit
 * above the provider. Shows a loader while a google-only session is verified so
 * a stranger never sees the app shell before being ejected to /auth. Password
 * and multi-identity sessions pass through instantly with no extra network calls.
 */
/** Rebinds the location push subscription on group change; mounted inside LocationSessionProvider so it covers every guarded route. */
const LocationPushRebindMount = (): null => {
  useLocationPushRebind();
  return null;
};

/** Mounted once inside BrowserRouter (needs router context for useLocation);
 * fires first-party product-analytics events. See src/lib/analytics.ts. */
const AnalyticsMount = (): null => {
  useAnalytics();
  return null;
};

const GuardedLayout = (): JSX.Element => {
  const { checking, ejectError } = useGoogleAccountGuard();

  if (ejectError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <p className="text-lg font-semibold text-foreground">Couldn't sign you out</p>
        <p className="max-w-sm text-sm text-muted-foreground">{ejectError.message}</p>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <OrbState state="working" size="full" />
      </div>
    );
  }

  return (
    <LocationSessionProvider>
      <LocationPushRebindMount />
      <LocationRouteEnforcer>
        <RestrictedRouteEnforcer>
          <AccountantRouteEnforcer>
            <LocationGate>
              <Layout />
            </LocationGate>
          </AccountantRouteEnforcer>
        </RestrictedRouteEnforcer>
      </LocationRouteEnforcer>
    </LocationSessionProvider>
  );
};

const App = (): JSX.Element => {
  const { loading, isAuthenticated } = useAuth();

  // Public widget routes never depend on auth state (they're rendered
  // unconditionally in the public routes block below) — don't block their
  // first paint on supabase.auth.getSession() resolving.
  const isWidgetRoute = window.location.pathname.startsWith("/widget");
  
  // MOCK-ONLY: preview/local builds skip auth loading for their standalone shell.
  const isMockRoute = Boolean(MockRouteLayout) && window.location.pathname.startsWith("/mock/");

  if (loading && !isWidgetRoute && !isMockRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <OrbState state="working" size="full" />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        storageKey="teddy-kids-theme"
      >
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true
            }}
          >
          <AnalyticsMount />
          <LazyLoadErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
            {/* ========================================= */}
            {/* PUBLIC ROUTES - No auth required         */}
            {/* ========================================= */}
            {PUBLIC_ROUTES.filter(r => !r.path.startsWith("/mock/")).map((route) => routeFor(route))}

            {/* ========================================= */}
            {/* MOCK ROUTES - Rendered outside auth gate */}
            {/* No Supabase, no auth, just theme + UI    */}
            {/* ========================================= */}
            {MockRouteLayout && <Route path="/mock" element={<MockRouteLayout />}>
              {PUBLIC_ROUTES.filter(r => r.path.startsWith("/mock/")).map((route) => {
                if (route.kind === 'page') {
                  const Component = route.element;
                  const relativePath = route.path.replace(/^\/mock\//, '');
                  return (
                    <Route
                      key={route.path}
                      path={relativePath}
                      element={<Component />}
                    />
                  );
                }
                return null;
              })}
            </Route>}

            {/* ========================================= */}
            {/* PROTECTED ROUTES - Auth required         */}
            {/* ========================================= */}
            {isAuthenticated ? (
              <Route path="/" element={<GuardedLayout />}>
                <Route index element={<RootRoute />} />
                {AUTHENTICATED_ROUTES.filter(r => !r.path.startsWith("/mock/")).map((route) => routeFor(route, { nested: true }))}
              </Route>
            ) : (
              <>
                {/* Redirect root to auth when not logged in */}
                <Route path="/" element={<Navigate to="/auth" replace />} />
                {/* Redirect any protected route to auth */}
                <Route path="/staff/*" element={<Navigate to="/auth" replace />} />
                <Route path="/issues/*" element={<Navigate to="/auth" replace />} />
                <Route path="/contracts/*" element={<Navigate to="/auth" replace />} />
                <Route path="/labs/*" element={<Navigate to="/auth" replace />} />
                <Route path="/grow/*" element={<Navigate to="/auth" replace />} />
              </>
            )}

            {/* Catch-all for 404 */}
            <Route path="*" element={isAuthenticated ? <NotFound /> : <Navigate to="/auth" replace />} />
              </Routes>
            </Suspense>
          </LazyLoadErrorBoundary>
        </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
// HMR Test - Contract Compliance System Ready
